import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  MenuItem, Select,  FormControl, InputLabel, SelectChangeEvent
} from "@mui/material";

import axios from "axios";
import { get, put } from "src/services/httpClient.service";

const API_URL = process.env.REACT_APP_BASE_API_URL;
//const API_URL = `${APIURL}/api/modules`;

interface Module {
  id:'',
  moduleId: '',
  vendorId: '',
}

interface EditModuleProps {
  module: Module | null;
  handleClose?: () => void;
  onSubmit?: () => void;
  fetchModulesData?: () => void;
}

const EditApplicationModule: React.FC<EditModuleProps> = ({
  module,
  handleClose,
  onSubmit,
  fetchModulesData,
}) => {
  const [formData, setFormData] = useState({
    vendorId: "",
    moduleId:""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [applicationModuleId, setApplicationModuleId] = useState("");

  const [vendorsList, setVendors] = useState([]);
  const [modulesList, setModules] = useState([]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await get(`${API_URL}/api/vendors`);
  
        setVendors(response);
      } catch (err) {
        console.error('Error fetching vendors:', err);
        setError('Failed to load vendors.');
      }
    };
  
  
    const fetchModules = async () => {
      try {
        const response = await get(`${API_URL}/api/modules/findAll`);
  
        setModules(response?.data);
      } catch (err) {
        console.error('Error fetching modules:', err);
        setError('Failed to load modules.');
      }
    };
  
    fetchVendors(); // ✅ Call it here, outside the function definition
    fetchModules();
  }, [API_URL]);
  
  // ✅ Prefill form data when module changes
  useEffect(() => {
    if (module) {
      setFormData({
        moduleId: module.moduleId,
        vendorId: module.vendorId
      });
      setApplicationModuleId(module.id);
    }
  }, [module]);

  // const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        ...formData
      };

      const response = await put(`${API_URL}/api/applicationModules/updateApplicationModule/${applicationModuleId}?vendorId=${formData.vendorId}&moduleId=${formData.moduleId}`, null);

      if (response.data) {
        setSuccess("✅ Application Module updated successfully!");
        if (fetchModulesData) fetchModulesData();
        if (onSubmit) onSubmit();
      }
    } catch (err: any) {
      console.error("Error updating application module:", err);
      const errorMessage =
        err.response?.data?.message || "Failed to update application module";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
    ) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name as string]: value as string,
      }));
    };

  const handleCancel = () => {
    if (handleClose) handleClose();
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <form onSubmit={handleSubmit}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Vendor</InputLabel>
              <Select
                name="vendorId"
                value={formData.vendorId}
                onChange={(e: SelectChangeEvent<string>) => handleChange(e)}
                label="Vendor"
              >
                {vendorsList.length > 0 ? (
                  vendorsList.map((vendor) => (
                    <MenuItem key={vendor.vendorId} value={vendor.vendorId}>
                      {vendor.vendorName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No vendors available</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

            <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Module</InputLabel>
              <Select
                name="moduleId"
                value={formData.moduleId}
                onChange={(e: SelectChangeEvent<string>) => handleChange(e)}
                label="Module"
              >
                {modulesList.length > 0 ? (
                  modulesList.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      {module.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No modules available</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
          >
            <Button onClick={handleCancel} disabled={loading} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Update Module"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default EditApplicationModule;
